import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { api } from '@/lib/api';
import BubbleCard from '@/components/BubbleCard';
import AdSlot from '@/components/AdSlot';
import EventBanner from '@/components/EventBanner';
import WhispersStrip from '@/components/WhispersStrip';
import GhostBanner from '@/components/GhostBanner';
import RoamingBanner from '@/components/RoamingBanner';
import FounderBadge from '@/components/FounderBadge';
import { FireSimple, Star, Heart, Lightning, Waves, ChatCircleText, PaperPlaneRight, Sparkle } from '@phosphor-icons/react';

const REACTIONS = [
  { key: 'fire', icon: FireSimple, color: '#ff5f00' },
  { key: 'star', icon: Star, color: '#ffd700' },
  { key: 'heart', icon: Heart, color: '#ff007f' },
  { key: 'spark', icon: Lightning, color: '#ccff00' },
  { key: 'wave', icon: Waves, color: '#00f0ff' },
];

const PostCard = ({ post, onReact, onOpenComments }) => {
  const total = Object.values(post.reactions || {}).reduce((a, b) => a + b, 0);
  return (
    <article className="snap-start min-h-[calc(100vh-9rem)] flex items-center px-4 sm:px-8 py-8">
      <div className="w-full max-w-3xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
        <div className="md:col-span-5 flex justify-center">
          {post.bubble ? (
            <Link data-testid={`post-bubble-${post.id}`} to={`/bubble/${post.bubble.id}`}>
              <BubbleCard bubble={post.bubble} size="lg" />
            </Link>
          ) : (
            <div className="h-56 w-56 rounded-full bg-gradient-to-br from-bx-cyan/30 to-bx-magenta/30 breathe" />
          )}
        </div>
        <div className="md:col-span-7">
          <div className="text-xs uppercase tracking-widest text-white/60 mb-2">
            {post.kind === 'transfer' ? 'Energy passed' : 'Story'}
          </div>
          <p className="font-display text-2xl sm:text-3xl font-bold leading-tight">{post.text}</p>
          <div className="mt-3 text-sm text-white/60">
            <span className="text-bx-cyan font-bold">@{post.from_username}</span>
            <FounderBadge username={post.from_username} className="ml-1" />
            {post.to_username && <> → <span className="text-bx-magenta font-bold">@{post.to_username}</span><FounderBadge username={post.to_username} className="ml-1" /></>}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-2">
            {REACTIONS.map((r) => (
              <button
                key={r.key}
                data-testid={`react-${post.id}-${r.key}`}
                onClick={() => onReact(post.id, r.key)}
                className="flex items-center gap-1 px-3 py-2 rounded-full glass-hi hover:bg-white/10 transition text-sm font-bold"
              >
                <r.icon size={16} color={r.color} weight="fill" />
                <span className="text-white/70">{post.reactions?.[r.key] || 0}</span>
              </button>
            ))}
            <button
              data-testid={`comment-${post.id}`}
              onClick={() => onOpenComments(post)}
              className="flex items-center gap-1 px-3 py-2 rounded-full glass-hi hover:bg-white/10 transition text-sm font-bold"
            >
              <ChatCircleText size={16} weight="fill" />
              <span className="text-white/70">{post.comments || 0}</span>
            </button>
            <div className="ml-auto text-[10px] uppercase tracking-widest text-white/55">
              {total} reactions
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const Composer = ({ onPost }) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try {
      await onPost(text.trim());
      setText('');
    } finally { setBusy(false); }
  };
  return (
    <div className="snap-start min-h-[200px] px-4 py-8">
      <div className="max-w-3xl mx-auto glass rounded-2xl p-5 flex items-center gap-3">
        <Sparkle size={20} className="text-bx-volt" weight="fill" />
        <input
          data-testid="composer-input"
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && submit()}
          placeholder="Share a moment with your bubbles…"
          className="flex-1 bg-transparent outline-none text-sm"
        />
        <button data-testid="composer-submit" onClick={submit} disabled={busy} className="btn-primary px-4 py-2 rounded-full text-sm flex items-center gap-1">
          Post <PaperPlaneRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
};

const CommentSheet = ({ open, post, onClose }) => {
  const [comments, setComments] = useState([]);
  const [text, setText] = useState('');

  const load = useCallback(async () => {
    if (!post) return;
    const { data } = await api.get(`/feed/${post.id}/comments`);
    setComments(data.comments);
  }, [post]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const send = async () => {
    if (!text.trim()) return;
    try {
      const { data } = await api.post(`/feed/${post.id}/comment`, { text: text.trim() });
      setComments((c) => [...c, data.comment]);
      setText('');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'comment blocked');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm grid place-items-end sm:place-items-center"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="glass rounded-t-3xl sm:rounded-3xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col"
          >
            <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-display font-bold">Comments</h3>
              <button data-testid="comments-close" onClick={onClose} className="text-white/65">close</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {comments.length === 0 && <div className="text-white/60 text-sm">Be the first to drop a comment.</div>}
              {comments.map((c) => (
                <div key={c.id} className="text-sm">
                  <span className="text-bx-cyan font-bold">@{c.username}</span><FounderBadge username={c.username} className="ml-0.5" />{' '}
                  <span className="text-white/80">{c.text}</span>
                </div>
              ))}
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input
                data-testid="comment-input"
                value={text} onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder="add a comment…"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-sm outline-none focus:border-bx-cyan"
              />
              <button data-testid="comment-submit" onClick={send} className="btn-primary px-4 rounded-full text-sm">Send</button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [openPost, setOpenPost] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get('/feed');
    setPosts(data.posts);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onReact = async (id, emoji) => {
    const { data } = await api.post(`/feed/${id}/react?emoji=${emoji}`);
    setPosts((arr) => arr.map((p) => (p.id === id ? { ...p, reactions: data.post.reactions } : p)));
  };

  const onPost = async (text) => {
    try {
      await api.post('/feed/post', { text });
      await load();
      toast.success('Posted');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'post blocked');
    }
  };

  return (
    <div data-testid="feed-root" className="feed-snap pb-24">
      <div className="max-w-3xl mx-auto px-4 sm:px-8 pt-6 space-y-4">
        <GhostBanner />
        <EventBanner />
        <WhispersStrip />
      </div>
      <Composer onPost={onPost} />
      {posts.map((p, idx) => (
        <React.Fragment key={p.id}>
          <PostCard post={p} onReact={onReact} onOpenComments={setOpenPost} />
          {idx > 0 && (idx + 1) % 5 === 0 && <AdSlot placement="feed" />}
        </React.Fragment>
      ))}
      {posts.length === 0 && (
        <div className="text-center text-white/60 py-20">No posts yet. Blow a bubble and pass it forward.</div>
      )}
      <CommentSheet open={!!openPost} post={openPost} onClose={() => setOpenPost(null)} />
    </div>
  );
};

export default Feed;
